import React from 'react';
import { Box, Breakpoint, Typography } from '@mui/material';
import { getLevelColours, ScoreLevelName } from '../../interfaces/INetworkScore';

interface LevelDiscProps {
    level: ScoreLevelName;
    levelNumber: number;
    /** API's `badgeColourKey` for this level, when known — see {@link getLevelColours}. */
    badgeColourKey?: string;
    size?: number | Partial<Record<Breakpoint, number>>;
    /** Number only, without the "LEVEL" caption — for small discs such as the profile panel. */
    compact?: boolean;
}

/**
 * The solid "level disc" (network-score.md, section 6) — the big number-in-a-circle used on the
 * Network Score card and the future Network Score screen. Colour comes solely from
 * `badgeColourKey`, so an admin renaming or re-colouring a level needs no frontend change; `level`
 * is used only for the accessible label.
 */
export const LevelDisc: React.FC<LevelDiscProps> = ({ level, levelNumber, badgeColourKey, size = 88, compact = false }) => {
    const colours = getLevelColours(badgeColourKey);

    return (
        <Box
            role="img"
            aria-label={`Level ${levelNumber}, ${level}`}
            sx={{
                width: size,
                height: size,
                borderRadius: '50%',
                bgcolor: colours.solidBg,
                color: colours.solidText,
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                justifyContent: 'center',
                flexShrink: 0,
            }}
        >
            <Typography variant={compact ? 'h5' : 'h4'} component="span" sx={{ lineHeight: 1, fontWeight: 700 }}>
                {levelNumber}
            </Typography>
            {!compact && (
                <Typography variant="caption" sx={{ letterSpacing: '0.08em', opacity: 0.85 }}>
                    LEVEL
                </Typography>
            )}
        </Box>
    );
};

export default LevelDisc;
